import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Zap, Eye, EyeOff, X, Bell, BellOff, Loader2 } from 'lucide-react';

// ============= ULTRA-FAST DEBOUNCED INPUT =============
// Local state for immediate response, debounced parent update
const DebouncedInput = ({ value, onChange, delay = 300, type = "number", ...props }) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef(null);
  
  // Sync local value when parent value changes externally
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleChange = useCallback((e) => {
    const newValue = type === "number" ? (Number(e.target.value) || 0) : e.target.value;
    setLocalValue(newValue); // Immediate local update
    
    // Debounce parent update
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      onChange({ target: { value: newValue } });
    }, delay);
  }, [onChange, delay, type]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  
  return <Input type={type} value={localValue} onChange={handleChange} {...props} />;
};

// ============= WEB WORKER MANAGEMENT =============
let smartFilterWorker = null;
let workerRequestId = 0;
const pendingRequests = new Map();

// Initialize Web Worker
const getWorker = () => {
  if (!smartFilterWorker && typeof Worker !== 'undefined') {
    try {
      smartFilterWorker = new Worker('/smartFilterWorker.js');
      smartFilterWorker.onmessage = (e) => {
        const { type, requestId, result, error } = e.data;
        const pending = pendingRequests.get(requestId);
        if (pending) {
          pendingRequests.delete(requestId);
          if (type === 'FILTER_RESULT') {
            pending.resolve(result);
          } else if (type === 'FILTER_ERROR') {
            pending.reject(new Error(error));
          }
        }
      };
      smartFilterWorker.onerror = (error) => {
        console.error('Smart Filter Worker error:', error);
      };
    } catch (e) {
      console.warn('Web Worker not available, using main thread:', e);
    }
  }
  return smartFilterWorker;
};

// Telegram alert cooldown tracking (client-side)
const _telegramAlertCooldown = new Map(); // symbol -> lastAlertTime
const TELEGRAM_COOLDOWN_MS = 60000; // 1 minute

// Basic filter telegram alert cooldown (client-side)
const _basicFilterAlertCooldown = new Map(); // symbol_filter -> lastAlertTime
const BASIC_FILTER_COOLDOWN_MS = 30000; // 30 seconds

// Send Telegram alert for smart filter match
export const sendSmartFilterTelegramAlert = async (apiClient, symbol, token, filterNames, tickData) => {
  // Client-side cooldown check
  const lastAlert = _telegramAlertCooldown.get(symbol) || 0;
  const now = Date.now();
  if (now - lastAlert < TELEGRAM_COOLDOWN_MS) {
    return { status: 'cooldown', remaining: Math.ceil((TELEGRAM_COOLDOWN_MS - (now - lastAlert)) / 1000) };
  }
  
  try {
    const response = await apiClient.post('/user/telegram/smart-filter-alert', {
      symbol,
      token,
      filter_names: filterNames,
      tick_data: tickData
    });
    
    if (response.data.status === 'sent') {
      _telegramAlertCooldown.set(symbol, now);
    }
    
    return response.data;
  } catch (error) {
    console.error('Failed to send telegram alert:', error);
    return { status: 'error', reason: error.message };
  }
};

// Send Telegram alert for basic filter match
export const sendBasicFilterTelegramAlert = async (apiClient, symbol, token, filterType, filterValue, tickData, tableId = null) => {
  // Client-side cooldown check
  const cooldownKey = `${symbol}_${filterType}`;
  const lastAlert = _basicFilterAlertCooldown.get(cooldownKey) || 0;
  const now = Date.now();
  if (now - lastAlert < BASIC_FILTER_COOLDOWN_MS) {
    return { status: 'cooldown', remaining: Math.ceil((BASIC_FILTER_COOLDOWN_MS - (now - lastAlert)) / 1000) };
  }
  
  try {
    const response = await apiClient.post('/user/telegram/basic-filter-alert', {
      symbol,
      token,
      filter_type: filterType,
      filter_value: filterValue,
      tick_data: tickData,
      table_id: tableId
    });
    
    if (response.data.status === 'sent') {
      _basicFilterAlertCooldown.set(cooldownKey, now);
    }
    
    return response.data;
  } catch (error) {
    console.error('Failed to send basic filter telegram alert:', error);
    return { status: 'error', reason: error.message };
  }
};

// Default filter state
export const DEFAULT_SMART_FILTERS = {
  enabled: false,
  mode: 'highlight', // 'filter' or 'highlight'
  showLabels: false, // Show labels with filter name, or just highlight
  
  levelSweep: {
    enabled: false,
    useBarPercent: false,
    ltqNumber: 500,
    ltqBarPercent: 80,
    sweepType: 'either',
  },
  
  consecutiveHigh: {
    enabled: false,
    useBarPercent: false,
    ltqNumber: 500,
    ltqBarPercent: 80,
    minTicks: 3,
  },
  
  volumeSpike: {
    enabled: false,
    useLTQBarPercent: false,
    ltqNumber: 500,
    ltqBarPercent: 80,
    useVolBarPercent: false,
    volNumber: 5000,
    volBarPercent: 80,
  },
  
  iceberg: {
    enabled: false,
    useBarPercent: false,
    ltqNumber: 200,
    ltqBarPercent: 50,
    minTicks: 5,
    priceTolerance: 0.5,
  },
  
  algoBurst: {
    enabled: false,
    useBarPercent: false,
    ltqNumber: 300,
    ltqBarPercent: 70,
    minTradesPerSecond: 3,
  },
  
  // HIGH LIQUIDITY: Tight spread detection
  highLiquidity: {
    enabled: false,
    maxSpread: 0.5, // Spread <= this = high liquidity
    ltqEnabled: false, // Optional LTQ filter
    ltqNumber: 500, // Min LTQ threshold
  },
  
  // LOW LIQUIDITY: Wide spread detection  
  lowLiquidity: {
    enabled: false,
    useTopPercent: true,
    topPercent: 20, // Top 20% highest spreads
    minSpread: 5.0, // Or fixed minimum spread
    ltqEnabled: false, // Optional LTQ filter
    ltqNumber: 500, // Min LTQ threshold
  },
  
  // HIGH LTQ: Large trade quantity detection
  highLTQ: {
    enabled: false,
    useBarPercent: false,
    ltqNumber: 1000, // Fixed LTQ threshold
    ltqBarPercent: 90, // Or top 10% (90th percentile)
  },
  
  // HIGH VOLUME CHANGE: Large volume change detection
  highVolChange: {
    enabled: false,
    useBarPercent: true,
    volNumber: 5000, // Fixed volume change threshold
    volBarPercent: 80, // Top 20% (80th percentile)
  },
  
  // Telegram alert settings
  telegramAlerts: false,
};

// Filter names for display
const FILTER_NAMES = {
  levelSweep: 'Sweep',
  consecutiveHigh: 'Consec',
  volumeSpike: 'Vol+LTQ',
  iceberg: 'Iceberg',
  algoBurst: 'Algo',
  highLiquidity: 'Hi-Liq',
  lowLiquidity: 'Lo-Liq',
  highLTQ: 'Hi-LTQ',
  highVolChange: 'Hi-Vol',
};

// Calculate max values from ticks - HYBRID: Use yesterday's baseline if provided
// yesterdayMax: { maxLTQ, maxVolChange, maxOIChange, maxBidQty, maxAskQty, maxSpread }
export const calculateMaxValues = (ticks, yesterdayMax = null) => {
  if (!ticks || ticks.length === 0) {
    // If no ticks, use yesterday's values or defaults
    return yesterdayMax || { maxLTQ: 1, maxVolChange: 1, maxOIChange: 1, maxBidQty: 1, maxAskQty: 1, maxSpread: 1 };
  }
  
  // Sample for large datasets
  const sampleSize = Math.min(ticks.length, 5000);
  const step = Math.max(1, Math.floor(ticks.length / sampleSize));
  
  let maxLTQ = 0;
  let maxVolChange = 0;
  let maxOIChange = 0;
  let maxBidQty = 0;
  let maxAskQty = 0;
  let maxSpread = 0;
  
  for (let i = 0; i < ticks.length; i += step) {
    const tick = ticks[i];
    const ltq = tick.last_traded_quantity || 0;
    const volChg = Math.abs(tick.volume_change || 0);
    const oiChg = Math.abs(tick.oi_change || 0);
    const bidQty = tick.bid_qty || 0;
    const askQty = tick.ask_qty || 0;
    const spread = tick.spread || 0;
    
    if (ltq > maxLTQ) maxLTQ = ltq;
    if (volChg > maxVolChange) maxVolChange = volChg;
    if (oiChg > maxOIChange) maxOIChange = oiChg;
    if (bidQty > maxBidQty) maxBidQty = bidQty;
    if (askQty > maxAskQty) maxAskQty = askQty;
    if (spread > maxSpread) maxSpread = spread;
  }
  
  // HYBRID: Use MAX of (today's calculated, yesterday's baseline)
  // This ensures early market ticks don't show 100% bars
  if (yesterdayMax) {
    maxLTQ = Math.max(maxLTQ, yesterdayMax.maxLTQ || 0);
    maxVolChange = Math.max(maxVolChange, yesterdayMax.maxVolChange || 0);
    maxOIChange = Math.max(maxOIChange, yesterdayMax.maxOIChange || 0);
    maxBidQty = Math.max(maxBidQty, yesterdayMax.maxBidQty || 0);
    maxAskQty = Math.max(maxAskQty, yesterdayMax.maxAskQty || 0);
    maxSpread = Math.max(maxSpread, yesterdayMax.maxSpread || 0);
  }
  
  return { 
    maxLTQ: maxLTQ || 1, 
    maxVolChange: maxVolChange || 1,
    maxOIChange: maxOIChange || 1,
    maxBidQty: maxBidQty || 1,
    maxAskQty: maxAskQty || 1,
    maxSpread: maxSpread || 1
  };
};

// Get LTQ threshold
const getLTQThreshold = (filter, maxLTQ) => {
  return filter.useBarPercent 
    ? maxLTQ * (filter.ltqBarPercent / 100) 
    : filter.ltqNumber;
};

// OPTIMIZED: Check Level Sweep
// Logic: LTQ >= ltqThreshold AND (LTQ = Bid_Qty OR LTQ = Ask_Qty)
// A sweep occurs when LTQ EXACTLY matches the bid/ask qty (someone took all liquidity)
const checkLevelSweep = (tick, filter, maxLTQ) => {
  const ltqThreshold = getLTQThreshold(filter, maxLTQ);
  const ltq = tick.last_traded_quantity || 0;
  
  // Step 1: Check if LTQ >= threshold
  if (ltq < ltqThreshold) return false;
  
  const bidQty = tick.bid_qty || 0;
  const askQty = tick.ask_qty || 0;
  
  // Step 2: Check if LTQ EXACTLY matches Bid Qty or Ask Qty (0% tolerance)
  // LTQ = Bid_Qty (for Sell Sweep - someone hit the bid exactly)
  const matchesBid = bidQty > 0 && ltq === bidQty;
  
  // LTQ = Ask_Qty (for Buy Sweep - someone lifted the ask exactly)  
  const matchesAsk = askQty > 0 && ltq === askQty;
  
  // Return based on sweep type selection
  if (filter.sweepType === 'bid') return matchesBid;
  if (filter.sweepType === 'ask') return matchesAsk;
  return matchesBid || matchesAsk;  // Either
};

// OPTIMIZED: Pre-compute streak info for all ticks
// IMPORTANT: This function now sorts by timestamp internally to detect
// time-based consecutive patterns regardless of display sort order
const precomputeStreaks = (ticks, ltqThreshold) => {
  const streakInfo = new Array(ticks.length).fill(0);
  
  if (!ticks || ticks.length === 0) return streakInfo;
  
  // Create sorted indices by timestamp
  const sortedIndices = ticks
    .map((tick, index) => ({ index, timestamp_ms: tick.timestamp_ms || 0 }))
    .sort((a, b) => a.timestamp_ms - b.timestamp_ms)
    .map(item => item.index);
  
  // Track which original indices are part of a streak
  const streakGroups = new Map(); // Maps original index -> streak length
  
  let currentStreak = 0;
  let streakIndices = [];
  
  for (let i = 0; i < sortedIndices.length; i++) {
    const originalIndex = sortedIndices[i];
    const tick = ticks[originalIndex];
    const ltq = tick.last_traded_quantity || 0;
    
    if (ltq >= ltqThreshold) {
      currentStreak++;
      streakIndices.push(originalIndex);
    } else {
      // Mark all ticks in the completed streak with the streak length
      if (currentStreak > 0) {
        for (const idx of streakIndices) {
          streakGroups.set(idx, currentStreak);
        }
      }
      currentStreak = 0;
      streakIndices = [];
    }
  }
  
  // Handle final streak
  if (currentStreak > 0) {
    for (const idx of streakIndices) {
      streakGroups.set(idx, currentStreak);
    }
  }
  
  // Transfer to streakInfo array
  for (const [idx, streakLen] of streakGroups) {
    streakInfo[idx] = streakLen;
  }
  
  return streakInfo;
};

// OPTIMIZED: Check Volume Spike
const checkVolumeSpike = (tick, filter, maxLTQ, maxVolChange) => {
  const ltqThreshold = filter.useLTQBarPercent 
    ? maxLTQ * (filter.ltqBarPercent / 100)
    : filter.ltqNumber;
  
  const volThreshold = filter.useVolBarPercent
    ? maxVolChange * (filter.volBarPercent / 100)
    : filter.volNumber;
  
  const ltq = tick.last_traded_quantity || 0;
  const volChg = Math.abs(tick.volume_change || 0);
  
  return ltq >= ltqThreshold && volChg >= volThreshold;
};

// HIGH LIQUIDITY: Check if spread is tight (high liquidity) + optional LTQ
const checkHighLiquidity = (tick, filter) => {
  const spread = tick.spread || 0;
  // High liquidity = spread is less than or equal to threshold
  const spreadMatch = spread > 0 && spread <= (filter.maxSpread || 0.5);
  
  if (!spreadMatch) return false;
  
  // If LTQ filter is enabled, also check LTQ
  if (filter.ltqEnabled) {
    const ltq = tick.last_traded_quantity || 0;
    return ltq >= (filter.ltqNumber || 500);
  }
  
  return true;
};

// LOW LIQUIDITY: Check if spread is wide (low liquidity) + optional LTQ
const checkLowLiquidity = (tick, filter, maxSpread) => {
  const spread = tick.spread || 0;
  if (spread <= 0) return false;
  
  let spreadMatch = false;
  if (filter.useTopPercent) {
    // Top X% of spreads = spread >= (100 - topPercent)% of maxSpread
    const threshold = maxSpread * ((100 - (filter.topPercent || 20)) / 100);
    spreadMatch = spread >= threshold;
  } else {
    // Fixed minimum spread threshold
    spreadMatch = spread >= (filter.minSpread || 5.0);
  }
  
  if (!spreadMatch) return false;
  
  // If LTQ filter is enabled, also check LTQ
  if (filter.ltqEnabled) {
    const ltq = tick.last_traded_quantity || 0;
    return ltq >= (filter.ltqNumber || 500);
  }
  
  return true;
};

// HIGH LTQ: Check if LTQ is unusually high
const checkHighLTQ = (tick, filter, maxLTQ) => {
  const ltq = tick.last_traded_quantity || 0;
  if (ltq <= 0) return false;
  
  // Get threshold based on mode
  const threshold = filter.useBarPercent 
    ? (maxLTQ * (filter.ltqBarPercent || 90) / 100)
    : (filter.ltqNumber || 1000);
  
  return ltq >= threshold;
};

// HIGH VOLUME CHANGE: Check if volume change is unusually high
const checkHighVolChange = (tick, filter, maxVolChange) => {
  const volChange = Math.abs(tick.volume_change || 0);
  if (volChange <= 0) return false;
  
  // Get threshold based on mode
  const threshold = filter.useBarPercent 
    ? (maxVolChange * (filter.volBarPercent || 80) / 100)
    : (filter.volNumber || 5000);
  
  return volChange >= threshold;
};

// OPTIMIZED: Pre-compute iceberg patterns
const precomputeIceberg = (ticks, ltqThreshold, minTicks, priceTolerance) => {
  const icebergInfo = new Array(ticks.length).fill(false);
  
  for (let i = 0; i < ticks.length - minTicks + 1; i++) {
    const tick = ticks[i];
    if ((tick.last_traded_quantity || 0) < ltqThreshold) continue;
    
    const basePrice = tick.last_price || 0;
    let count = 1;
    
    for (let j = i + 1; j < ticks.length && count < minTicks + 5; j++) {
      const t = ticks[j];
      const priceDiff = Math.abs((t.last_price || 0) - basePrice);
      if (priceDiff <= priceTolerance && (t.last_traded_quantity || 0) >= ltqThreshold) {
        count++;
      } else {
        break;
      }
    }
    
    if (count >= minTicks) {
      for (let k = i; k < i + count && k < ticks.length; k++) {
        icebergInfo[k] = true;
      }
      i += count - 1;
    }
  }
  
  return icebergInfo;
};

// OPTIMIZED: Pre-compute algo burst patterns
const precomputeAlgoBurst = (ticks, ltqThreshold, minTrades) => {
  const burstInfo = new Array(ticks.length).fill(false);
  
  for (let i = 0; i < ticks.length; i++) {
    const tick = ticks[i];
    if ((tick.last_traded_quantity || 0) < ltqThreshold) continue;
    
    const baseTime = tick.timestamp_ms || new Date(tick.timestamp).getTime();
    const tradesInWindow = [i];
    
    for (let j = i + 1; j < ticks.length; j++) {
      const t = ticks[j];
      const tickTime = t.timestamp_ms || new Date(t.timestamp).getTime();
      const timeDiff = Math.abs(baseTime - tickTime);
      
      if (timeDiff <= 1000 && (t.last_traded_quantity || 0) >= ltqThreshold) {
        tradesInWindow.push(j);
      } else if (timeDiff > 1000) {
        break;
      }
    }
    
    if (tradesInWindow.length >= minTrades) {
      for (const idx of tradesInWindow) {
        burstInfo[idx] = true;
      }
    }
  }
  
  return burstInfo;
};

// ============= ULTRA-FAST SMART FILTER USING WEB WORKER =============
// Cache for filter results - longer cache for better performance
const filterResultsCache = new Map();
const CACHE_TTL = 5000; // 5 seconds cache (increased from 2s)

// Generate cache key
const getCacheKey = (ticksLength, filters) => {
  return `${ticksLength}_${JSON.stringify(filters)}`;
};

// ASYNC: Apply filters using Web Worker (non-blocking)
export const applySmartFiltersAsync = async (ticks, filters, maxValues) => {
  if (!filters.enabled || !ticks || ticks.length === 0) {
    return ticks.map(tick => ({ ...tick, _smartFilterMatch: false, _matchedFilters: [] }));
  }
  
  // Check cache first
  const cacheKey = getCacheKey(ticks.length, filters);
  const cached = filterResultsCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return applyFilterResultsToTicks(ticks, cached.result);
  }
  
  const worker = getWorker();
  
  if (worker && ticks.length > 100) {
    // Use Web Worker for datasets > 100 ticks (lowered threshold for better performance)
    const requestId = ++workerRequestId;
    
    return new Promise((resolve, reject) => {
      pendingRequests.set(requestId, { resolve, reject });
      
      // Timeout after 5 seconds
      setTimeout(() => {
        if (pendingRequests.has(requestId)) {
          pendingRequests.delete(requestId);
          // Fallback to sync
          resolve(applySmartFiltersSync(ticks, filters, maxValues));
        }
      }, 5000);
      
      worker.postMessage({
        type: 'FILTER',
        requestId,
        ticks,
        filters,
        maxValues
      });
    }).then(result => {
      // Cache result
      filterResultsCache.set(cacheKey, { result, timestamp: Date.now() });
      return applyFilterResultsToTicks(ticks, result);
    });
  } else {
    // Use sync for small datasets
    return applySmartFiltersSync(ticks, filters, maxValues);
  }
};

// Apply filter results to ticks (fast - just marking)
const applyFilterResultsToTicks = (ticks, result) => {
  const { matchedIndices, matchedFiltersMap } = result;
  const matchedSet = new Set(matchedIndices);
  
  return ticks.map((tick, index) => ({
    ...tick,
    _smartFilterMatch: matchedSet.has(index),
    _matchedFilters: matchedFiltersMap[index] || [],
  }));
};

// SYNC: Fast synchronous filter (for small datasets or fallback)
const applySmartFiltersSync = (ticks, filters, maxValues) => {
  if (!filters.enabled || !ticks || ticks.length === 0) {
    return ticks.map(tick => ({ ...tick, _smartFilterMatch: false, _matchedFilters: [] }));
  }
  
  const { maxLTQ, maxVolChange, maxSpread } = maxValues;
  
  // Process ALL ticks - no sampling/limit
  // Web Worker handles large datasets efficiently off main thread
  const processedTicks = ticks;
  
  // Pre-compute expensive pattern detections
  let streakInfo = null;
  let icebergInfo = null;
  let burstInfo = null;
  
  if (filters.consecutiveHigh?.enabled) {
    const ltqThreshold = getLTQThreshold(filters.consecutiveHigh, maxLTQ);
    streakInfo = precomputeStreaks(processedTicks, ltqThreshold);
  }
  
  if (filters.iceberg?.enabled) {
    const ltqThreshold = getLTQThreshold(filters.iceberg, maxLTQ);
    icebergInfo = precomputeIceberg(processedTicks, ltqThreshold, filters.iceberg.minTicks, filters.iceberg.priceTolerance);
  }
  
  if (filters.algoBurst?.enabled) {
    const ltqThreshold = getLTQThreshold(filters.algoBurst, maxLTQ);
    burstInfo = precomputeAlgoBurst(processedTicks, ltqThreshold, filters.algoBurst.minTradesPerSecond);
  }
  
  // Process ticks - FIRST PASS: determine matches (ALL ticks)
  const matchResults = ticks.map((tick, index) => {
    const matchedFilters = [];
    
    if (filters.levelSweep?.enabled && checkLevelSweep(tick, filters.levelSweep, maxLTQ)) {
      matchedFilters.push('levelSweep');
    }
    
    if (filters.consecutiveHigh?.enabled && streakInfo && streakInfo[index] >= filters.consecutiveHigh.minTicks) {
      matchedFilters.push('consecutiveHigh');
    }
    
    if (filters.volumeSpike?.enabled && checkVolumeSpike(tick, filters.volumeSpike, maxLTQ, maxVolChange)) {
      matchedFilters.push('volumeSpike');
    }
    
    if (filters.iceberg?.enabled && icebergInfo && icebergInfo[index]) {
      matchedFilters.push('iceberg');
    }
    
    if (filters.algoBurst?.enabled && burstInfo && burstInfo[index]) {
      matchedFilters.push('algoBurst');
    }
    
    // NEW: High Liquidity filter (tight spread)
    if (filters.highLiquidity?.enabled && checkHighLiquidity(tick, filters.highLiquidity)) {
      matchedFilters.push('highLiquidity');
    }
    
    // NEW: Low Liquidity filter (wide spread)
    if (filters.lowLiquidity?.enabled && checkLowLiquidity(tick, filters.lowLiquidity, maxSpread)) {
      matchedFilters.push('lowLiquidity');
    }
    
    // NEW: High LTQ filter (large trades)
    if (filters.highLTQ?.enabled && checkHighLTQ(tick, filters.highLTQ, maxLTQ)) {
      matchedFilters.push('highLTQ');
    }
    
    // NEW: High Volume Change filter
    if (filters.highVolChange?.enabled && checkHighVolChange(tick, filters.highVolChange, maxVolChange)) {
      matchedFilters.push('highVolChange');
    }
    
    return { match: matchedFilters.length > 0, filters: matchedFilters };
  });
  
  // SECOND PASS: Mark first/last in each group for BOX styling
  const result = ticks.map((tick, index) => {
    const { match, filters: matchedFilters } = matchResults[index];
    
    if (!match) {
      return { ...tick, _smartFilterMatch: false, _matchedFilters: [] };
    }
    
    // Check if previous row is NOT a match (this is FIRST in group)
    const isFirstInGroup = index === 0 || !matchResults[index - 1].match;
    
    // Check if next row is NOT a match (this is LAST in group)
    const isLastInGroup = index === ticks.length - 1 || !matchResults[index + 1].match;
    
    return {
      ...tick,
      _smartFilterMatch: true,
      _matchedFilters: matchedFilters,
      _isFirstInGroup: isFirstInGroup,
      _isLastInGroup: isLastInGroup,
    };
  });
  
  return result;
};

// LEGACY SYNC: Keep for backwards compatibility (renamed)
export const applySmartFilters = (ticks, filters, maxValues) => {
  return applySmartFiltersSync(ticks, filters, maxValues);
};

// Get row class - BOX STYLE: Top border on FIRST, Bottom border on LAST in group
export const getSmartFilterRowClass = (tick, filters) => {
  if (!filters.enabled || !tick._smartFilterMatch) return '';
  
  // Base styles: left/right borders ONLY - NO background color
  // Add extra left padding for vertical label
  let classes = 'border-l-2 border-r-2 border-l-yellow-400 border-r-yellow-400';
  
  // TOP border only on FIRST row in group
  if (tick._isFirstInGroup) {
    classes += ' border-t-2 border-t-yellow-400';
  }
  
  // BOTTOM border only on LAST row in group
  if (tick._isLastInGroup) {
    classes += ' border-b-2 border-b-yellow-400';
  }
  
  return classes;
};

// Get vertical filter label for first row in group
export const getVerticalFilterLabel = (tick, isFirstInGroup) => {
  // This function is no longer used - replaced by horizontal header rows
  return null;
};

// Get horizontal filter header row for first row in group - CLEAN SIMPLE DESIGN
// Only shows if showLabels is true
export const getFilterHeaderRow = (tick, isFirstInGroup, compact = false, showLabels = true) => {
  if (!showLabels) return null; // Don't show labels if disabled
  if (!tick._smartFilterMatch || !tick._matchedFilters || tick._matchedFilters.length === 0) return null;
  if (!isFirstInGroup) return null;
  
  // Get full filter names for display
  const fullNames = {
    'levelSweep': 'LEVEL SWEEP DETECTED',
    'consecutiveHigh': 'CONSECUTIVE HIGH LTQ',
    'volumeSpike': 'VOLUME + LTQ SPIKE',
    'iceberg': 'ICEBERG ORDER DETECTED',
    'algoBurst': 'ALGO BURST PATTERN',
    'highLiquidity': 'HIGH LIQUIDITY (TIGHT SPREAD)',
    'lowLiquidity': 'LOW LIQUIDITY (WIDE SPREAD)',
    'highLTQ': 'HIGH LTQ DETECTED',
    'highVolChange': 'HIGH VOLUME CHANGE'
  };
  
  // Simple bright colors - easy to read
  const filterColors = {
    'levelSweep': { text: '#E879F9', icon: '🎯' },      // Bright pink/purple
    'consecutiveHigh': { text: '#22D3EE', icon: '📊' }, // Bright cyan
    'volumeSpike': { text: '#4ADE80', icon: '📈' },     // Bright green
    'iceberg': { text: '#60A5FA', icon: '🧊' },         // Bright blue
    'algoBurst': { text: '#FB923C', icon: '⚡' },       // Bright orange
    'highLiquidity': { text: '#34D399', icon: '💧' },   // Green - liquid
    'lowLiquidity': { text: '#F87171', icon: '🏜️' },    // Red - dry/illiquid
    'highLTQ': { text: '#FBBF24', icon: '🔥' },         // Yellow/Gold - hot trade
    'highVolChange': { text: '#A78BFA', icon: '📊' }    // Purple - volume spike
  };
  
  // Use first matched filter for primary color
  const primaryFilter = tick._matchedFilters[0];
  const colors = filterColors[primaryFilter] || filterColors['consecutiveHigh'];
  
  const filterText = tick._matchedFilters.map(f => fullNames[f] || f).join(' | ');
  const icons = tick._matchedFilters.map(f => filterColors[f]?.icon || '⚡').join(' ');
  
  return (
    <div 
      className={`w-full flex items-center justify-between ${compact ? 'h-5' : 'h-6'} bg-[#0d0d0d] border-t-2 border-b-2 border-yellow-400`}
    >
      <div className="flex items-center gap-2 px-3">
        <span className={`${compact ? 'text-sm' : 'text-base'}`}>{icons}</span>
        <span 
          className={`${compact ? 'text-[10px]' : 'text-[12px]'} font-bold tracking-wide uppercase`}
          style={{ color: colors.text }}
        >
          {filterText}
        </span>
      </div>
      <div className="flex items-center gap-1 px-3">
        <span className={`${compact ? 'text-[8px]' : 'text-[10px]'} font-bold text-yellow-400`}>
          ● SCAN ALERT
        </span>
      </div>
    </div>
  );
};

// Get filter badge for matched filters (horizontal)
export const getFilterBadges = (tick) => {
  if (!tick._matchedFilters || tick._matchedFilters.length === 0) return null;
  
  return tick._matchedFilters.map(f => (
    <span key={f} className="px-1 py-0.5 text-[8px] font-bold rounded bg-yellow-500/30 text-yellow-300 border border-yellow-500/50 mr-1">
      {FILTER_NAMES[f]}
    </span>
  ));
};

// ============= PER-SYMBOL SETTINGS STORAGE =============

// Save filter settings for a specific symbol
export const saveFilterSettings = (symbol, filters) => {
  if (!symbol) return;
  try {
    const key = `smartfilter_${symbol}`;
    localStorage.setItem(key, JSON.stringify(filters));
  } catch (e) {
    console.error('Failed to save filter settings:', e);
  }
};

// Load filter settings for a specific symbol
export const loadFilterSettings = (symbol) => {
  if (!symbol) return null;
  try {
    const key = `smartfilter_${symbol}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load filter settings:', e);
  }
  return null;
};

// Get all saved symbol settings
export const getAllSavedSymbols = () => {
  const symbols = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('smartfilter_')) {
        symbols.push(key.replace('smartfilter_', ''));
      }
    }
  } catch (e) {
    console.error('Failed to get saved symbols:', e);
  }
  return symbols;
};

// Delete saved settings for a symbol
export const deleteFilterSettings = (symbol) => {
  if (!symbol) return;
  try {
    const key = `smartfilter_${symbol}`;
    localStorage.removeItem(key);
  } catch (e) {
    console.error('Failed to delete filter settings:', e);
  }
};

// Compact Smart Filters Panel with Loading State and Per-Symbol Save
export const SmartFiltersPanel = ({ 
  filters, 
  setFilters, 
  isCompact = false, 
  isProcessing = false,
  currentSymbol = null,  // Current symbol for per-symbol settings
  onSymbolChange = null  // Callback when symbol changes
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [savedSymbols, setSavedSymbols] = useState([]);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  
  // Load saved symbols list on mount
  useEffect(() => {
    setSavedSymbols(getAllSavedSymbols());
  }, []);
  
  // Auto-save when filters change (if symbol is set)
  useEffect(() => {
    if (currentSymbol && filters.enabled) {
      // Debounce save
      const timer = setTimeout(() => {
        saveFilterSettings(currentSymbol, filters);
        if (!savedSymbols.includes(currentSymbol)) {
          setSavedSymbols(prev => [...prev, currentSymbol]);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [filters, currentSymbol, savedSymbols]);
  
  // Load settings when symbol changes
  useEffect(() => {
    if (currentSymbol) {
      const saved = loadFilterSettings(currentSymbol);
      if (saved) {
        setFilters(saved);
      }
    }
  }, [currentSymbol, setFilters]);
  
  // Auto-enable master switch when any filter configuration is changed
  const updateFilter = useCallback((filterName, updates) => {
    setFilters(prev => {
      // If updating a filter's settings and that filter is enabled, auto-enable the master switch
      const filterIsEnabled = prev[filterName]?.enabled;
      return {
        ...prev,
        [filterName]: { ...prev[filterName], ...updates },
        enabled: filterIsEnabled ? true : prev.enabled,
      };
    });
  }, [setFilters]);
  
  const toggleFilter = useCallback((filterName) => {
    setFilters(prev => {
      const newFilterEnabled = !prev[filterName].enabled;
      return {
        ...prev,
        [filterName]: { ...prev[filterName], enabled: newFilterEnabled },
        // Auto-enable master switch when any filter is turned ON
        enabled: newFilterEnabled ? true : prev.enabled,
      };
    });
  }, [setFilters]);
  
  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_SMART_FILTERS);
  }, [setFilters]);
  
  const activeCount = [
    filters.levelSweep?.enabled,
    filters.consecutiveHigh?.enabled,
    filters.volumeSpike?.enabled,
    filters.iceberg?.enabled,
    filters.algoBurst?.enabled,
    filters.highLiquidity?.enabled,
    filters.lowLiquidity?.enabled,
    filters.highLTQ?.enabled,
    filters.highVolChange?.enabled,
  ].filter(Boolean).length;

  return (
    <div className="relative inline-block">
      <div className="flex items-center gap-1">
        <Button
          ref={buttonRef}
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className={`${isCompact ? 'h-6 w-6 p-0' : 'h-7 px-2'} text-xs ${activeCount > 0 ? 'bg-yellow-900/30 border-yellow-600/50 text-yellow-400' : 'bg-[#0a0a0a] border-[#333]'}`}
          title={`Smart Filters${activeCount > 0 ? ` (${activeCount} active)` : ''}`}
        >
          {isProcessing ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Zap className={`w-3 h-3 ${isCompact ? '' : 'mr-1'}`} />
          )}
          {!isCompact && <>Smart {activeCount > 0 && `(${activeCount})`}</>}
        </Button>
        
        {/* Show controls when filters are active - both compact and non-compact modes */}
        {activeCount > 0 && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters(prev => ({ ...prev, enabled: !prev.enabled }))}
              className={`${isCompact ? 'h-5 px-1' : 'h-6 px-1'} ${filters.enabled ? 'text-green-400' : 'text-[#52525B]'}`}
              title={filters.enabled ? 'Disable filters' : 'Enable filters'}
            >
              {filters.enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            </Button>
            
            <Select value={filters.mode} onValueChange={(v) => setFilters(prev => ({ ...prev, mode: v }))}>
              <SelectTrigger className={`${isCompact ? 'h-5 w-16' : 'h-6 w-20'} text-[10px] bg-[#0a0a0a] border-[#333]`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[10000]" position="popper" sideOffset={5}>
                <SelectItem value="highlight">Highlight</SelectItem>
                <SelectItem value="filter">Filter</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Label ON/OFF Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters(prev => ({ ...prev, showLabels: !prev.showLabels }))}
              className={`${isCompact ? 'h-5 px-1.5' : 'h-6 px-2'} text-[9px] font-bold ${
                filters.showLabels 
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' 
                  : 'bg-[#1a1a1a] border-[#333] text-[#666]'
              }`}
              title={filters.showLabels ? 'Labels ON - showing filter names' : 'Labels OFF - highlight only'}
            >
              {isCompact ? (filters.showLabels ? 'LBL' : 'LBL') : `Label ${filters.showLabels ? 'ON' : 'OFF'}`}
            </Button>
          </>
        )}
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[10000] w-[420px] p-4 bg-[#111] border border-[#333] rounded-lg shadow-2xl max-h-[80vh] overflow-y-auto"
          style={{ 
            top: '100px',  // Fixed position below header
            right: '20px',  // Right aligned
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-base font-bold text-white">🎯 Smart Filters</span>
            <div className="flex items-center gap-2">
              {isProcessing && (
                <span className="text-[10px] text-yellow-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Processing...
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-6 px-2 text-[10px] text-red-400 hover:bg-red-900/30">
                <X className="w-3 h-3 mr-1" /> Clear All
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="h-6 px-1 hover:bg-[#333]">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Filter 1: Level Sweep */}
          <div className="mb-3 p-3 border border-[#333] rounded-lg bg-[#0a0a0a]">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={filters.levelSweep?.enabled} onCheckedChange={() => toggleFilter('levelSweep')} className="h-5 w-5" />
              <span className="text-sm font-bold text-cyan-400">1. Level Sweep</span>
            </div>
            {filters.levelSweep?.enabled && (
              <div className="space-y-3 ml-7 mt-3 p-3 bg-[#0f0f0f] rounded border border-[#222]">
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">LTQ:</Label>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={!filters.levelSweep.useBarPercent} onChange={() => updateFilter('levelSweep', { useBarPercent: false })} className="w-4 h-4 accent-cyan-500" />
                    <span className="text-[10px] text-[#666]">Number</span>
                    <DebouncedInput type="number" value={filters.levelSweep.ltqNumber} onChange={(e) => updateFilter('levelSweep', { ltqNumber: Number(e.target.value) || 0 })} disabled={filters.levelSweep.useBarPercent} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={filters.levelSweep.useBarPercent} onChange={() => updateFilter('levelSweep', { useBarPercent: true })} className="w-4 h-4 accent-cyan-500" />
                    <span className="text-[10px] text-[#666]">Bar%</span>
                    <DebouncedInput type="number" value={filters.levelSweep.ltqBarPercent} onChange={(e) => updateFilter('levelSweep', { ltqBarPercent: Number(e.target.value) || 0 })} disabled={!filters.levelSweep.useBarPercent} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" max={100} />
                    <span className="text-xs text-[#666]">%</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">Type:</Label>
                  <Select value={filters.levelSweep.sweepType} onValueChange={(v) => updateFilter('levelSweep', { sweepType: v })}>
                    <SelectTrigger className="h-7 w-28 text-xs bg-[#0a0a0a] border-[#444]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[10001]" position="popper">
                      <SelectItem value="bid">Bid (Sell Sweep)</SelectItem>
                      <SelectItem value="ask">Ask (Buy Sweep)</SelectItem>
                      <SelectItem value="either">Either</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          
          {/* Filter 2: Consecutive High */}
          <div className="mb-3 p-3 border border-[#333] rounded-lg bg-[#0a0a0a]">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={filters.consecutiveHigh?.enabled} onCheckedChange={() => toggleFilter('consecutiveHigh')} className="h-5 w-5" />
              <span className="text-sm font-bold text-green-400">2. Consecutive High LTQ</span>
            </div>
            {filters.consecutiveHigh?.enabled && (
              <div className="space-y-3 ml-7 mt-3 p-3 bg-[#0f0f0f] rounded border border-[#222]">
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">LTQ:</Label>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={!filters.consecutiveHigh.useBarPercent} onChange={() => updateFilter('consecutiveHigh', { useBarPercent: false })} className="w-4 h-4 accent-green-500" />
                    <span className="text-[10px] text-[#666]">Number</span>
                    <DebouncedInput type="number" value={filters.consecutiveHigh.ltqNumber} onChange={(e) => updateFilter('consecutiveHigh', { ltqNumber: Number(e.target.value) || 0 })} disabled={filters.consecutiveHigh.useBarPercent} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={filters.consecutiveHigh.useBarPercent} onChange={() => updateFilter('consecutiveHigh', { useBarPercent: true })} className="w-4 h-4 accent-green-500" />
                    <span className="text-[10px] text-[#666]">Bar%</span>
                    <DebouncedInput type="number" value={filters.consecutiveHigh.ltqBarPercent} onChange={(e) => updateFilter('consecutiveHigh', { ltqBarPercent: Number(e.target.value) || 0 })} disabled={!filters.consecutiveHigh.useBarPercent} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" max={100} />
                    <span className="text-xs text-[#666]">%</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">Ticks:</Label>
                  <DebouncedInput type="number" value={filters.consecutiveHigh.minTicks} onChange={(e) => updateFilter('consecutiveHigh', { minTicks: Number(e.target.value) || 2 })} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" min={2} />
                  <span className="text-xs text-[#666]">(consecutive in a row)</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Filter 3: Volume + LTQ Spike */}
          <div className="mb-3 p-3 border border-[#333] rounded-lg bg-[#0a0a0a]">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={filters.volumeSpike?.enabled} onCheckedChange={() => toggleFilter('volumeSpike')} className="h-5 w-5" />
              <span className="text-sm font-bold text-purple-400">3. Volume + LTQ Spike</span>
            </div>
            {filters.volumeSpike?.enabled && (
              <div className="space-y-3 ml-7 mt-3 p-3 bg-[#0f0f0f] rounded border border-[#222]">
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">LTQ:</Label>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={!filters.volumeSpike.useLTQBarPercent} onChange={() => updateFilter('volumeSpike', { useLTQBarPercent: false })} className="w-4 h-4 accent-purple-500" />
                    <span className="text-[10px] text-[#666]">Number</span>
                    <DebouncedInput type="number" value={filters.volumeSpike.ltqNumber} onChange={(e) => updateFilter('volumeSpike', { ltqNumber: Number(e.target.value) || 0 })} disabled={filters.volumeSpike.useLTQBarPercent} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={filters.volumeSpike.useLTQBarPercent} onChange={() => updateFilter('volumeSpike', { useLTQBarPercent: true })} className="w-4 h-4 accent-purple-500" />
                    <span className="text-[10px] text-[#666]">Bar%</span>
                    <DebouncedInput type="number" value={filters.volumeSpike.ltqBarPercent} onChange={(e) => updateFilter('volumeSpike', { ltqBarPercent: Number(e.target.value) || 0 })} disabled={!filters.volumeSpike.useLTQBarPercent} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" max={100} />
                    <span className="text-xs text-[#666]">%</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">Volume:</Label>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={!filters.volumeSpike.useVolBarPercent} onChange={() => updateFilter('volumeSpike', { useVolBarPercent: false })} className="w-4 h-4 accent-purple-500" />
                    <span className="text-[10px] text-[#666]">Number</span>
                    <DebouncedInput type="number" value={filters.volumeSpike.volNumber} onChange={(e) => updateFilter('volumeSpike', { volNumber: Number(e.target.value) || 0 })} disabled={filters.volumeSpike.useVolBarPercent} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={filters.volumeSpike.useVolBarPercent} onChange={() => updateFilter('volumeSpike', { useVolBarPercent: true })} className="w-4 h-4 accent-purple-500" />
                    <span className="text-[10px] text-[#666]">Bar%</span>
                    <DebouncedInput type="number" value={filters.volumeSpike.volBarPercent} onChange={(e) => updateFilter('volumeSpike', { volBarPercent: Number(e.target.value) || 0 })} disabled={!filters.volumeSpike.useVolBarPercent} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" max={100} />
                    <span className="text-xs text-[#666]">%</span>
                  </div>
                </div>
                <p className="text-[10px] text-[#555] ml-16">ℹ️ Triggers when BOTH conditions are met</p>
              </div>
            )}
          </div>
          
          {/* Filter 4: Iceberg */}
          <div className="mb-3 p-3 border border-[#333] rounded-lg bg-[#0a0a0a]">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={filters.iceberg?.enabled} onCheckedChange={() => toggleFilter('iceberg')} className="h-5 w-5" />
              <span className="text-sm font-bold text-blue-400">4. Iceberg Detection</span>
            </div>
            {filters.iceberg?.enabled && (
              <div className="space-y-3 ml-7 mt-3 p-3 bg-[#0f0f0f] rounded border border-[#222]">
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">LTQ:</Label>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={!filters.iceberg.useBarPercent} onChange={() => updateFilter('iceberg', { useBarPercent: false })} className="w-4 h-4 accent-blue-500" />
                    <span className="text-[10px] text-[#666]">Number</span>
                    <DebouncedInput type="number" value={filters.iceberg.ltqNumber} onChange={(e) => updateFilter('iceberg', { ltqNumber: Number(e.target.value) || 0 })} disabled={filters.iceberg.useBarPercent} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={filters.iceberg.useBarPercent} onChange={() => updateFilter('iceberg', { useBarPercent: true })} className="w-4 h-4 accent-blue-500" />
                    <span className="text-[10px] text-[#666]">Bar%</span>
                    <DebouncedInput type="number" value={filters.iceberg.ltqBarPercent} onChange={(e) => updateFilter('iceberg', { ltqBarPercent: Number(e.target.value) || 0 })} disabled={!filters.iceberg.useBarPercent} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" max={100} />
                    <span className="text-xs text-[#666]">%</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">Ticks:</Label>
                  <DebouncedInput type="number" value={filters.iceberg.minTicks} onChange={(e) => updateFilter('iceberg', { minTicks: Number(e.target.value) || 5 })} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" min={3} />
                  <span className="text-xs text-[#666]">(same price ticks)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">Tolerance:</Label>
                  <DebouncedInput type="number" value={filters.iceberg.tolerance} onChange={(e) => updateFilter('iceberg', { tolerance: Number(e.target.value) || 0.5 })} step="0.1" className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                  <span className="text-xs text-[#666]">± points</span>
                </div>
                <p className="text-[10px] text-[#555] ml-16">ℹ️ Detects hidden large orders at same price</p>
              </div>
            )}
          </div>
          
          {/* Filter 5: Algo Burst */}
          <div className="mb-3 p-3 border border-[#333] rounded-lg bg-[#0a0a0a]">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={filters.algoBurst?.enabled} onCheckedChange={() => toggleFilter('algoBurst')} className="h-5 w-5" />
              <span className="text-sm font-bold text-orange-400">5. Algo Burst (1-Sec)</span>
            </div>
            {filters.algoBurst?.enabled && (
              <div className="space-y-3 ml-7 mt-3 p-3 bg-[#0f0f0f] rounded border border-[#222]">
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">LTQ:</Label>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={!filters.algoBurst.useBarPercent} onChange={() => updateFilter('algoBurst', { useBarPercent: false })} className="w-4 h-4 accent-orange-500" />
                    <span className="text-[10px] text-[#666]">Number</span>
                    <DebouncedInput type="number" value={filters.algoBurst.ltqNumber} onChange={(e) => updateFilter('algoBurst', { ltqNumber: Number(e.target.value) || 0 })} disabled={filters.algoBurst.useBarPercent} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={filters.algoBurst.useBarPercent} onChange={() => updateFilter('algoBurst', { useBarPercent: true })} className="w-4 h-4 accent-orange-500" />
                    <span className="text-[10px] text-[#666]">Bar%</span>
                    <DebouncedInput type="number" value={filters.algoBurst.ltqBarPercent} onChange={(e) => updateFilter('algoBurst', { ltqBarPercent: Number(e.target.value) || 0 })} disabled={!filters.algoBurst.useBarPercent} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" max={100} />
                    <span className="text-xs text-[#666]">%</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">Trades:</Label>
                  <DebouncedInput type="number" value={filters.algoBurst.minTradesPerSecond} onChange={(e) => updateFilter('algoBurst', { minTradesPerSecond: Number(e.target.value) || 2 })} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" min={2} />
                  <span className="text-xs text-[#666]">per second (min)</span>
                </div>
                <p className="text-[10px] text-[#555] ml-16">ℹ️ Detects algorithmic execution patterns</p>
              </div>
            )}
          </div>
          
          {/* Filter 6: High Liquidity (Tight Spread) */}
          <div className="mb-3 p-3 border border-[#333] rounded-lg bg-[#0a0a0a]">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={filters.highLiquidity?.enabled} onCheckedChange={() => toggleFilter('highLiquidity')} className="h-5 w-5" />
              <span className="text-sm font-bold text-green-400">6. High Liquidity 💧</span>
            </div>
            {filters.highLiquidity?.enabled && (
              <div className="space-y-3 ml-7 mt-3 p-3 bg-[#0f0f0f] rounded border border-[#222]">
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-20">Max Spread:</Label>
                  <DebouncedInput type="number" value={filters.highLiquidity?.maxSpread || 0.5} onChange={(e) => updateFilter('highLiquidity', { maxSpread: Number(e.target.value) || 0.5 })} step="0.1" className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" min={0.01} />
                  <span className="text-xs text-[#666]">points</span>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-20">LTQ Filter:</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={filters.highLiquidity?.ltqEnabled} onCheckedChange={(checked) => updateFilter('highLiquidity', { ltqEnabled: checked })} className="h-4 w-4" />
                    <span className="text-[10px] text-[#666]">Enable</span>
                  </div>
                  {filters.highLiquidity?.ltqEnabled && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#666]">Min LTQ:</span>
                      <DebouncedInput type="number" value={filters.highLiquidity?.ltqNumber || 500} onChange={(e) => updateFilter('highLiquidity', { ltqNumber: Number(e.target.value) || 500 })} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-[#555] ml-20">ℹ️ Highlights ticks where spread ≤ {filters.highLiquidity?.maxSpread || 0.5} {filters.highLiquidity?.ltqEnabled ? `AND LTQ ≥ ${filters.highLiquidity?.ltqNumber || 500}` : ''}</p>
              </div>
            )}
          </div>
          
          {/* Filter 7: Low Liquidity (Wide Spread) */}
          <div className="mb-3 p-3 border border-[#333] rounded-lg bg-[#0a0a0a]">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={filters.lowLiquidity?.enabled} onCheckedChange={() => toggleFilter('lowLiquidity')} className="h-5 w-5" />
              <span className="text-sm font-bold text-red-400">7. Low Liquidity 🏜️</span>
            </div>
            {filters.lowLiquidity?.enabled && (
              <div className="space-y-3 ml-7 mt-3 p-3 bg-[#0f0f0f] rounded border border-[#222]">
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-20">Spread:</Label>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input type="radio" checked={filters.lowLiquidity?.useTopPercent} onChange={() => updateFilter('lowLiquidity', { useTopPercent: true })} className="w-4 h-4 accent-red-500" />
                      <span className="text-[10px] text-[#666]">Top %</span>
                      <DebouncedInput type="number" value={filters.lowLiquidity?.topPercent || 20} onChange={(e) => updateFilter('lowLiquidity', { topPercent: Number(e.target.value) || 20 })} disabled={!filters.lowLiquidity?.useTopPercent} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" min={1} max={50} />
                      <span className="text-xs text-[#666]">%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="radio" checked={!filters.lowLiquidity?.useTopPercent} onChange={() => updateFilter('lowLiquidity', { useTopPercent: false })} className="w-4 h-4 accent-red-500" />
                      <span className="text-[10px] text-[#666]">Min</span>
                      <DebouncedInput type="number" value={filters.lowLiquidity?.minSpread || 5} onChange={(e) => updateFilter('lowLiquidity', { minSpread: Number(e.target.value) || 5 })} disabled={filters.lowLiquidity?.useTopPercent} step="0.5" className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" min={0.5} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-20">LTQ Filter:</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={filters.lowLiquidity?.ltqEnabled} onCheckedChange={(checked) => updateFilter('lowLiquidity', { ltqEnabled: checked })} className="h-4 w-4" />
                    <span className="text-[10px] text-[#666]">Enable</span>
                  </div>
                  {filters.lowLiquidity?.ltqEnabled && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#666]">Min LTQ:</span>
                      <DebouncedInput type="number" value={filters.lowLiquidity?.ltqNumber || 500} onChange={(e) => updateFilter('lowLiquidity', { ltqNumber: Number(e.target.value) || 500 })} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-[#555] ml-20">ℹ️ Highlights ticks with wide spreads {filters.lowLiquidity?.ltqEnabled ? `AND LTQ ≥ ${filters.lowLiquidity?.ltqNumber || 500}` : ''} (illiquid market)</p>
              </div>
            )}
          </div>
          
          {/* Filter 8: High LTQ (Large Trade Quantity) */}
          <div className="mb-3 p-3 border border-[#333] rounded-lg bg-[#0a0a0a]">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={filters.highLTQ?.enabled} onCheckedChange={() => toggleFilter('highLTQ')} className="h-5 w-5" />
              <span className="text-sm font-bold text-yellow-400">8. High LTQ 🔥</span>
            </div>
            {filters.highLTQ?.enabled && (
              <div className="space-y-3 ml-7 mt-3 p-3 bg-[#0f0f0f] rounded border border-[#222]">
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">LTQ:</Label>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={!filters.highLTQ?.useBarPercent} onChange={() => updateFilter('highLTQ', { useBarPercent: false })} className="w-4 h-4 accent-yellow-500" />
                    <span className="text-[10px] text-[#666]">Number</span>
                    <DebouncedInput type="number" value={filters.highLTQ?.ltqNumber || 1000} onChange={(e) => updateFilter('highLTQ', { ltqNumber: Number(e.target.value) || 1000 })} disabled={filters.highLTQ?.useBarPercent} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={filters.highLTQ?.useBarPercent} onChange={() => updateFilter('highLTQ', { useBarPercent: true })} className="w-4 h-4 accent-yellow-500" />
                    <span className="text-[10px] text-[#666]">Top%</span>
                    <DebouncedInput type="number" value={filters.highLTQ?.ltqBarPercent || 90} onChange={(e) => updateFilter('highLTQ', { ltqBarPercent: Number(e.target.value) || 90 })} disabled={!filters.highLTQ?.useBarPercent} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" max={100} />
                    <span className="text-xs text-[#666]">%</span>
                  </div>
                </div>
                <p className="text-[10px] text-[#555] ml-16">ℹ️ Highlights unusually large trades (potential operator/institutional activity)</p>
              </div>
            )}
          </div>
          
          {/* Filter 9: High Volume Change */}
          <div className="mb-3 p-3 border border-[#333] rounded-lg bg-[#0a0a0a]">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={filters.highVolChange?.enabled} onCheckedChange={() => toggleFilter('highVolChange')} className="h-5 w-5" />
              <span className="text-sm font-bold text-purple-400">9. High Volume Change 📊</span>
            </div>
            {filters.highVolChange?.enabled && (
              <div className="space-y-3 ml-7 mt-3 p-3 bg-[#0f0f0f] rounded border border-[#222]">
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-[#888] w-16">Vol Δ:</Label>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={!filters.highVolChange?.useBarPercent} onChange={() => updateFilter('highVolChange', { useBarPercent: false })} className="w-4 h-4 accent-purple-500" />
                    <span className="text-[10px] text-[#666]">Number</span>
                    <DebouncedInput type="number" value={filters.highVolChange?.volNumber || 5000} onChange={(e) => updateFilter('highVolChange', { volNumber: Number(e.target.value) || 5000 })} disabled={filters.highVolChange?.useBarPercent} className="h-7 w-20 text-xs bg-[#0a0a0a] border-[#444] px-2" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={filters.highVolChange?.useBarPercent} onChange={() => updateFilter('highVolChange', { useBarPercent: true })} className="w-4 h-4 accent-purple-500" />
                    <span className="text-[10px] text-[#666]">Top%</span>
                    <DebouncedInput type="number" value={filters.highVolChange?.volBarPercent || 80} onChange={(e) => updateFilter('highVolChange', { volBarPercent: Number(e.target.value) || 80 })} disabled={!filters.highVolChange?.useBarPercent} className="h-7 w-16 text-xs bg-[#0a0a0a] border-[#444] px-2" max={100} />
                    <span className="text-xs text-[#666]">%</span>
                  </div>
                </div>
                <p className="text-[10px] text-[#555] ml-16">ℹ️ Highlights ticks with high volume change (accumulation/distribution)</p>
              </div>
            )}
          </div>
          
          {/* Save/Load Presets Section */}
          {currentSymbol && (
            <div className="mb-3 p-3 border border-[#444] rounded-lg bg-[#111]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#888]">Current:</span>
                  <span className="text-xs text-cyan-400 font-medium">{currentSymbol}</span>
                  {savedSymbols.includes(currentSymbol) && (
                    <span className="text-[8px] text-green-500 bg-green-500/20 px-1 py-0.5 rounded">SAVED</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      saveFilterSettings(currentSymbol, filters);
                      if (!savedSymbols.includes(currentSymbol)) {
                        setSavedSymbols(prev => [...prev, currentSymbol]);
                      }
                      setShowSaveConfirm(true);
                      setTimeout(() => setShowSaveConfirm(false), 1500);
                    }}
                    className="h-6 px-2 text-[10px] bg-green-600/20 border-green-600/50 text-green-400 hover:bg-green-600/30"
                  >
                    💾 Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const saved = loadFilterSettings(currentSymbol);
                      if (saved) {
                        setFilters(saved);
                      }
                    }}
                    className="h-6 px-2 text-[10px] bg-blue-600/20 border-blue-600/50 text-blue-400 hover:bg-blue-600/30"
                    disabled={!savedSymbols.includes(currentSymbol)}
                  >
                    📂 Load
                  </Button>
                </div>
              </div>
              {showSaveConfirm && (
                <div className="mt-2 text-[10px] text-green-400 text-center animate-pulse">
                  ✓ Settings saved for {currentSymbol}
                </div>
              )}
              
              {/* Load from other symbols */}
              {savedSymbols.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[#333]">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#666]">Load from:</span>
                    <Select onValueChange={(symbol) => {
                      const saved = loadFilterSettings(symbol);
                      if (saved) {
                        setFilters(saved);
                      }
                    }}>
                      <SelectTrigger className="h-6 w-36 text-[10px] bg-[#0a0a0a] border-[#333]">
                        <SelectValue placeholder="Select preset..." />
                      </SelectTrigger>
                      <SelectContent className="z-[10001]" position="popper">
                        {savedSymbols.map(sym => (
                          <SelectItem key={sym} value={sym} className="text-[10px]">
                            {sym}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Enable/Mode */}
          <div className="flex items-center justify-between pt-3 border-t border-[#333]">
            <div className="flex items-center gap-2">
              <Checkbox checked={filters.enabled} onCheckedChange={(v) => setFilters(prev => ({ ...prev, enabled: v }))} className="h-5 w-5" />
              <span className="text-sm text-white font-medium">Enable</span>
            </div>
            <Select value={filters.mode} onValueChange={(v) => setFilters(prev => ({ ...prev, mode: v }))}>
              <SelectTrigger className="h-7 w-28 text-xs bg-[#0a0a0a] border-[#444]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[10001]" position="popper">
                <SelectItem value="highlight">Highlight</SelectItem>
                <SelectItem value="filter">Filter Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Telegram Alerts Toggle */}
          <div className="flex items-center justify-between pt-2 border-t border-[#333] mt-2">
            <div className="flex items-center gap-2">
              <Button
                variant={filters.telegramAlerts ? "default" : "outline"}
                size="sm"
                onClick={() => setFilters(prev => ({ ...prev, telegramAlerts: !prev.telegramAlerts }))}
                className={`h-7 px-2 text-xs ${filters.telegramAlerts ? 'bg-blue-600 text-white' : 'bg-[#0a0a0a] border-[#444] text-[#888]'}`}
              >
                {filters.telegramAlerts ? <Bell className="w-3 h-3 mr-1" /> : <BellOff className="w-3 h-3 mr-1" />}
                Telegram Alert
              </Button>
            </div>
            <span className="text-[9px] text-[#666]">
              {filters.telegramAlerts ? 'Alerts ON' : 'Alerts OFF'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartFiltersPanel;
